/**
 * Day 2 match orchestrator — real ML pipeline + LLM for explanation only.
 *
 * Replaces the Day 1 `day1Match` hack (one LLM call returning everything).
 *
 * Flow:
 *   1. Raw text → parseResume / parseJobDescription (structured sections + bullets)
 *   2. scoreAll (seven independent signals, embeddings via @xenova)
 *   3. fuse → overall score 0-100 + verdict
 *   4. One Groq call to generate rationale + top strengths/gaps as evidence
 *      (the NUMBERS come from the engine, the LLM only writes English)
 */

import {
  BgeEmbedder,
  MsMarcoReranker,
  blendWithCrossEncoder,
  computeBiasDelta,
  fuse,
  maskPii,
  parseJobDescription,
  parseResume,
  scoreAll,
  totalBullets,
  verdictFromScore,
  type BiasDelta,
  type Bullet,
  type Embedder,
  type Evidence,
  type JobDescription,
  type Match,
  type PiiMaskResult,
  type Reranker,
  type Resume,
  type SubScores,
} from "@talent-align/engine";
import { z } from "zod";

import type { CompleteOptions } from "@/hooks/useOpenRouter";

// ---------------------------------------------------------------------------
// Embedder + reranker singletons — reused across matches so models load
// exactly once per page session.
// ---------------------------------------------------------------------------
let sharedEmbedder: Embedder | null = null;
let sharedReranker: Reranker | null = null;

function getEmbedder(): Embedder {
  if (!sharedEmbedder) sharedEmbedder = new BgeEmbedder();
  return sharedEmbedder;
}

function getReranker(): Reranker {
  if (!sharedReranker) sharedReranker = new MsMarcoReranker();
  return sharedReranker;
}

export async function warmEngine(): Promise<void> {
  const emb = getEmbedder();
  await emb.warm();
}

// ---------------------------------------------------------------------------
// LLM rationale + evidence — the only thing the LLM does on Day 2
// ---------------------------------------------------------------------------
const EvidenceSchema = z.object({
  rationale: z.string(),
  strengths: z
    .array(
      z.object({
        // Either an ID (preferred, e.g. "R4" / "J2") OR an inline quote.
        jd_id: z.string().nullable().optional(),
        resume_id: z.string().nullable().optional(),
        jd: z.string().optional(),
        resume: z.string().optional(),
        note: z.string().optional(),
      }),
    )
    .default([]),
  gaps: z
    .array(
      z.object({
        jd_id: z.string().nullable().optional(),
        jd: z.string().optional(),
        note: z.string().optional(),
      }),
    )
    .default([]),
});
type EvidenceResponse = z.infer<typeof EvidenceSchema>;

const EVIDENCE_SYSTEM = `You are TalentAlign's evidence writer.

The matching engine has already computed a numeric score via seven ML signals.
Your job is ONLY to explain it in plain English, and to pair concrete
evidence from the resume with the JD requirements it satisfies.

You output ONLY valid JSON — no prose, no markdown code fences, no commentary.

The resume and JD come with their bullets pre-numbered:
  R1, R2, R3... are resume bullets
  J1, J2, J3... are JD bullets

For each strength, cite BOTH a resume_id (R#) AND a jd_id (J#) — those are
the source-of-truth locators the UI uses to highlight the matched text.
For each gap, cite the jd_id of the requirement that's missing.

Only use IDs that appear in the numbered lists below. Never invent an ID.`;

function formatBulletsForPrompt(bullets: Bullet[], prefix: "R" | "J"): string {
  if (bullets.length === 0) return `(no ${prefix === "R" ? "resume" : "JD"} bullets extracted)`;
  return bullets
    .map((b, i) => `  ${prefix}${i + 1} [${b.section}] ${b.text}`)
    .join("\n");
}

function buildEvidencePrompt(
  resume: Resume,
  jd: JobDescription,
  overallScore: number,
  subScores: Record<string, number>,
): string {
  const RAW_MAX = 4000;
  const resumeRaw =
    resume.rawText.length > RAW_MAX ? resume.rawText.slice(0, RAW_MAX) : resume.rawText;
  const jdRaw = jd.rawText.length > RAW_MAX ? jd.rawText.slice(0, RAW_MAX) : jd.rawText;

  // Cap bullet lists so the prompt stays within Groq token budgets on
  // long documents. Real resumes have 10-40 bullets; real JDs 10-25.
  const resumeBullets = resume.bullets.slice(0, 40);
  const jdBullets = jd.bullets.slice(0, 25);

  return `=== RESUME (full text, for context) ===
${resumeRaw}

=== RESUME BULLETS (numbered — use these IDs in strengths[].resume_id) ===
${formatBulletsForPrompt(resumeBullets, "R")}

=== JOB DESCRIPTION (full text, for context) ===
${jdRaw}

=== JD BULLETS (numbered — use these IDs in strengths[].jd_id and gaps[].jd_id) ===
${formatBulletsForPrompt(jdBullets, "J")}

=== ENGINE SCORES (do not change) ===
Overall: ${overallScore.toFixed(0)}/100
Sub-scores: ${JSON.stringify(subScores)}

=== TASK ===
Return ONLY this JSON shape:

{
  "rationale": "<1-2 sentences explaining the score in plain English>",
  "strengths": [
    {"resume_id": "R4", "jd_id": "J2", "note": "<short why this matches>"}
  ],
  "gaps": [
    {"jd_id": "J7", "note": "<short why missing from resume>"}
  ]
}

Include 3-6 strengths and 1-4 gaps. Resume_id must be a real R# from the
numbered list above; jd_id must be a real J#. Never invent an ID.`;
}

type CompleteFn = (opts: CompleteOptions) => Promise<string>;

function tryParseJson(text: string): EvidenceResponse | null {
  let cleaned = text.trim();
  const fence = cleaned.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fence?.[1]) cleaned = fence[1].trim();
  const firstBrace = cleaned.indexOf("{");
  const lastBrace = cleaned.lastIndexOf("}");
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    cleaned = cleaned.slice(firstBrace, lastBrace + 1);
  }
  try {
    const parsed = JSON.parse(cleaned) as unknown;
    const result = EvidenceSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

async function generateEvidence(
  complete: CompleteFn,
  resume: Resume,
  jd: JobDescription,
  overallScore: number,
  subScores: Record<string, number>,
  model: string,
): Promise<{ response: EvidenceResponse; retried: boolean }> {
  const baseOpts: CompleteOptions = {
    messages: [
      { role: "system", content: EVIDENCE_SYSTEM },
      {
        role: "user",
        content: buildEvidencePrompt(resume, jd, overallScore, subScores),
      },
    ],
    temperature: 0.1,
    maxTokens: 2048,
    responseFormat: "json",
    model: model as CompleteOptions["model"],
  };

  let raw = await complete(baseOpts);
  let parsed = tryParseJson(raw);
  let retried = false;
  if (!parsed) {
    retried = true;
    raw = await complete({
      ...baseOpts,
      temperature: 0,
      messages: [
        {
          role: "system",
          content:
            EVIDENCE_SYSTEM +
            "\n\nCRITICAL: your previous output was not valid JSON. Return ONLY the JSON object.",
        },
        baseOpts.messages[1] ?? { role: "user", content: "" },
      ],
    });
    parsed = tryParseJson(raw);
  }
  if (!parsed) {
    return { response: { rationale: "", strengths: [], gaps: [] }, retried };
  }
  return { response: parsed, retried };
}

// Map an ID like "R4" / "J2" back to a bullet in the pre-numbered list.
// Falls back to null if the LLM hallucinates an out-of-range index.
function resolveBulletId(id: string | null | undefined, bullets: Bullet[]): Bullet | null {
  if (!id) return null;
  const match = id.match(/^[RJ](\d+)$/i);
  if (!match) return null;
  const idx = Number.parseInt(match[1] ?? "0", 10) - 1;
  if (idx < 0 || idx >= bullets.length) return null;
  return bullets[idx] ?? null;
}

function evidenceFromLlm(
  resp: EvidenceResponse,
  resume: Resume,
  jd: JobDescription,
): Evidence[] {
  const resumeBullets = resume.bullets.slice(0, 40);
  const jdBullets = jd.bullets.slice(0, 25);

  return [
    ...resp.strengths.map<Evidence>((s) => {
      const rb = resolveBulletId(s.resume_id ?? null, resumeBullets);
      const jb = resolveBulletId(s.jd_id ?? null, jdBullets);
      return {
        kind: "strength",
        jdLocator: jb
          ? {
              section: jb.section,
              bulletIndex: jb.index,
              text: jb.text,
            }
          : s.jd
            ? { section: "JD", text: s.jd }
            : null,
        resumeLocator: rb
          ? {
              section: rb.section,
              bulletIndex: rb.index,
              text: rb.text,
            }
          : s.resume
            ? { section: "Resume", text: s.resume }
            : null,
        weight: 1.0,
        note: s.note ?? "",
      };
    }),
    ...resp.gaps.map<Evidence>((g) => {
      const jb = resolveBulletId(g.jd_id ?? null, jdBullets);
      return {
        kind: "gap",
        jdLocator: jb
          ? {
              section: jb.section,
              bulletIndex: jb.index,
              text: jb.text,
            }
          : g.jd
            ? { section: "JD", text: g.jd }
            : null,
        resumeLocator: null,
        weight: 1.0,
        note: g.note ?? "",
      };
    }),
  ];
}

// ---------------------------------------------------------------------------
// Public: run a full match (parse → score → fuse → rerank → explain)
// ---------------------------------------------------------------------------
export interface EngineMatchResult {
  match: Match;
  latencyMs: number;
  retried: boolean;
  bulletCount: number;
  scoreLatencyMs: number;
  explainLatencyMs: number;
  /** Day 9: time spent in cross-encoder rerank. 0 if rerank was skipped. */
  rerankLatencyMs: number;
  // Day 3: keep the parsed documents so ChatPanel and AgentTimeline can
  // ground their follow-up calls in the same bullet IDs the evidence pass used.
  resume: Resume;
  jd: JobDescription;
}

export interface EngineMatchOptions {
  /** When true (default), run the cross-encoder reranker and blend its
   *  output into the final overallScore. When false, stop after the
   *  bi-encoder + LLM evidence pass. */
  useReranker?: boolean;
}

export async function engineMatch(
  complete: CompleteFn,
  resumeText: string,
  jdText: string,
  model: string = "llama-3.3-70b-versatile",
  options: EngineMatchOptions = {},
): Promise<EngineMatchResult> {
  const useReranker = options.useReranker ?? true;
  const start = performance.now();

  // 1. Parse
  const resume = parseResume(resumeText);
  const jd = parseJobDescription(jdText);

  // 2. Score — the real engine pipeline
  const scoreStart = performance.now();
  const embedder = getEmbedder();
  const subScores = await scoreAll(resume, jd, { embedder });
  const scoreLatencyMs = Math.round(performance.now() - scoreStart);

  // 3. Fuse bi-encoder sub-scores
  const biencoderScore = fuse(subScores);

  // 4. Cross-encoder rerank (Day 9) — optional, on by default
  let rerankLatencyMs = 0;
  let crossEncoderScore: number | undefined;
  let finalScore = biencoderScore;
  if (useReranker) {
    const rerankStart = performance.now();
    try {
      const reranker = getReranker();
      crossEncoderScore = await reranker.score(jd.rawText, resume.rawText);
      finalScore = blendWithCrossEncoder(biencoderScore, crossEncoderScore, 0.4);
    } catch (err) {
      // Cross-encoder can fail on slow networks / unsupported browsers.
      // Fall back to bi-encoder-only scoring rather than failing the match.
      console.warn("[match-engine] cross-encoder rerank failed, falling back", err);
    }
    rerankLatencyMs = Math.round(performance.now() - rerankStart);
  }

  const verdict = verdictFromScore(finalScore);

  // 5. LLM evidence + rationale (one call)
  const explainStart = performance.now();
  const { response: evidence, retried } = await generateEvidence(
    complete,
    resume,
    jd,
    finalScore,
    subScores as unknown as Record<string, number>,
    model,
  );
  const explainLatencyMs = Math.round(performance.now() - explainStart);

  const rerankTag = useReranker && crossEncoderScore !== undefined ? "+msmarco-ce" : "";
  const match: Match = {
    overallScore: finalScore,
    verdict,
    subScores,
    evidence: evidenceFromLlm(evidence, resume, jd),
    rationale: evidence.rationale,
    modelUsed: `groq:${model}+bge-small${rerankTag}`,
    promptVersion: "engine_v4_crossencoder",
    piiMasked: false,
    latencyMs: Math.round(performance.now() - start),
    crossEncoderScore,
    biencoderScore,
  };

  return {
    match,
    latencyMs: match.latencyMs ?? 0,
    retried,
    bulletCount: totalBullets(resume, jd),
    scoreLatencyMs,
    explainLatencyMs,
    rerankLatencyMs,
    resume,
    jd,
  };
}

// ---------------------------------------------------------------------------
// Day 4: bias-aware scoring
//
// Runs the engine twice — once on the original text, once on the PII-masked
// text — then computes a per-signal delta. Skips the LLM evidence pass on
// the masked run because we only care about the numeric comparison.
// ---------------------------------------------------------------------------
export interface BiasAuditResult {
  unmasked: {
    overallScore: number;
    subScores: SubScores;
  };
  masked: {
    overallScore: number;
    subScores: SubScores;
  };
  delta: BiasDelta;
  maskResume: PiiMaskResult;
  maskJd: PiiMaskResult;
  maskLatencyMs: number;
}

/**
 * Run the matching engine on both the original and PII-masked versions of a
 * resume + JD pair. Uses the existing embedder singleton so the unmasked
 * embeddings stay cached while the masked pass re-computes only what changed.
 */
export async function runBiasAudit(
  resumeText: string,
  jdText: string,
  unmaskedOverallScore: number,
  unmaskedSubScores: SubScores,
): Promise<BiasAuditResult> {
  const start = performance.now();

  // Strip PII from both sides
  const maskResume = maskPii(resumeText);
  const maskJd = maskPii(jdText);

  // Parse + score the masked versions. No LLM call on this pass —
  // we only need the numbers for the delta.
  const maskedResume = parseResume(maskResume.maskedText);
  const maskedJd = parseJobDescription(maskJd.maskedText);
  const embedder = getEmbedder();
  const maskedSubScores = await scoreAll(maskedResume, maskedJd, { embedder });
  const maskedOverallScore = fuse(maskedSubScores);

  const delta = computeBiasDelta(
    { overallScore: unmaskedOverallScore, subScores: unmaskedSubScores as unknown as Record<string, number> },
    { overallScore: maskedOverallScore, subScores: maskedSubScores as unknown as Record<string, number> },
  );

  return {
    unmasked: {
      overallScore: unmaskedOverallScore,
      subScores: unmaskedSubScores,
    },
    masked: {
      overallScore: maskedOverallScore,
      subScores: maskedSubScores,
    },
    delta,
    maskResume,
    maskJd,
    maskLatencyMs: Math.round(performance.now() - start),
  };
}
